import { Input as UIKitInput } from "@circle-react-uikit/Input";
import type { InputOrArea, InputProps } from "@circle-react-uikit/Input";
import { Divider } from "@circleco/compass/components/Divider";
import { NumberInput as CompassNumberInput } from "@circleco/compass/components/NumberInput";
import { PasswordInput as CompassPasswordInput } from "@circleco/compass/components/PasswordInput";
import { TextArea as CompassTextArea } from "@circleco/compass/components/TextArea";
import { TextInput as CompassTextInput } from "@circleco/compass/components/TextInput";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import { omit } from "lodash";
import type { ReactNode } from "react";
import { forwardRef } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { InputPrefixContext } from "./InputPrefixContext";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export type { InputOrArea, InputProps };

interface CompassInjectedProps {
  description?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
  label?: ReactNode;
  labelPosition?: "start" | "end";
  labelTooltip?: string;
  layout?: "horizontal" | "vertical";
  rootProps?: Record<string, unknown>;
}

type CompatInputProps = InputProps &
  CompassInjectedProps & {
    hideStepper?: boolean;
  };

const COMPASS_INJECTED_KEYS = [
  "label",
  "description",
  "error",
  "invalid",
  "labelPosition",
  "labelTooltip",
  "layout",
  "rootProps",
] as const;

const UIKIT_ONLY_KEYS = [
  "className",
  "otherClassName",
  "hideBorder",
  "noPadding",
  "focusRequired",
  "customOpacity",
  "prefix",
  "prefixWrapperClassName",
  "suffix",
  "suffixWrapperClassName",
  "suffixInnerClassName",
  "wrapperClassName",
  "variant",
] as const;

const KEYS_OMITTED_FOR_COMPASS = [...UIKIT_ONLY_KEYS, ...COMPASS_INJECTED_KEYS];

const resolveCompassFieldLayout = (
  layout: CompatInputProps["layout"],
  labelPosition: CompatInputProps["labelPosition"],
): "horizontal" | undefined => {
  if (layout === "horizontal") return "horizontal";
  if (layout === "vertical") return undefined;
  if (labelPosition === "end") return "horizontal";
  return undefined;
};

const buildPrefixSlot = (
  prefix: ReactNode | undefined,
  prefixWrapperClassName: string | undefined,
): ReactNode | undefined =>
  !prefix ? undefined : (
    <InputPrefixContext.Provider value>
      <div className="flex h-full max-w-[50%] shrink-0 gap-2">
        <div className="flex min-w-0 items-center">
          {prefixWrapperClassName ? (
            <div className={prefixWrapperClassName}>{prefix}</div>
          ) : (
            prefix
          )}
        </div>
        <Divider orientation="vertical" />
      </div>
    </InputPrefixContext.Provider>
  );

const buildSuffixSlot = (
  suffix: ReactNode | undefined,
  suffixWrapperClassName: string | undefined,
  suffixInnerClassName: string | undefined,
): ReactNode | undefined => {
  if (!suffix) return undefined;
  const inner = suffixInnerClassName ? (
    <span className={suffixInnerClassName}>{suffix}</span>
  ) : (
    suffix
  );
  const suffixContent = suffixWrapperClassName ? (
    <div className={suffixWrapperClassName}>{inner}</div>
  ) : (
    inner
  );
  return (
    <div className="flex h-full gap-2">
      <Divider orientation="vertical" />
      <div className="flex items-center">{suffixContent}</div>
    </div>
  );
};

const shouldRenderNativeHiddenInput = (input: {
  type?: string;
  className?: string;
  wrapperClassName?: string;
}) =>
  input.type === "hidden" ||
  input.wrapperClassName === "hidden" ||
  input.className === "hidden";

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

export const Input = forwardRef<InputOrArea, CompatInputProps>((props, ref) => {
  const isCompassInputEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  if (!isCompassInputEnabled) {
    return <UIKitInput ref={ref} {...omit(props, COMPASS_INJECTED_KEYS)} />;
  }

  const {
    className,
    prefix,
    prefixWrapperClassName,
    suffix,
    suffixWrapperClassName,
    suffixInnerClassName,
    wrapperClassName,
    variant = "text",
    label,
    description,
    error,
    invalid,
    labelPosition,
    labelTooltip,
    layout,
    rootProps: rootPropsFromProps,
    hideStepper = true,
    required,
  } = props;

  const rest = omit(props, KEYS_OMITTED_FOR_COMPASS);

  const compassLayout = resolveCompassFieldLayout(layout, labelPosition);

  const sharedCompassFieldProps = {
    label,
    description,
    error,
    invalid,
    labelTooltip,
    layout: compassLayout,
    rootProps: rootPropsFromProps,
    labelProps: required ? { required } : undefined,
  };

  if (
    shouldRenderNativeHiddenInput({
      type: props.type,
      wrapperClassName,
      className,
    })
  ) {
    return (
      <input ref={ref as React.Ref<HTMLInputElement>} {...rest} type="hidden" />
    );
  }

  if (variant === "textarea") {
    return (
      <CompassTextArea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        {...sharedCompassFieldProps}
        rootClassName={classNames(
          allowedClassesFor("TextArea", wrapperClassName ?? ""),
        )}
        {...rest}
      />
    );
  }

  if (props.type === "number") {
    const { name, value, defaultValue, min, max, step, onChange } = props;
    const numberRest = omit(rest, [
      "onChange",
      "onValueChange",
      "type",
      "value",
      "defaultValue",
      "min",
      "max",
      "step",
    ]);

    const handleValueChange = (numberValue: number | null) => {
      onChange?.({
        target: {
          name,
          value: numberValue === null ? "" : String(numberValue),
        },
      });
    };

    return (
      <CompassNumberInput
        ref={ref as React.Ref<HTMLInputElement>}
        {...sharedCompassFieldProps}
        startContent={buildPrefixSlot(prefix, prefixWrapperClassName)}
        endContent={buildSuffixSlot(
          suffix,
          suffixWrapperClassName,
          suffixInnerClassName,
        )}
        rootClassName={classNames(
          allowedClassesFor("NumberInput", wrapperClassName ?? ""),
        )}
        value={asNumber(value) ?? null}
        defaultValue={asNumber(defaultValue)}
        min={asNumber(min)}
        max={asNumber(max)}
        step={asNumber(step)}
        allowWheelScrub={false}
        onValueChange={handleValueChange}
        hideStepper={hideStepper}
        {...numberRest}
      />
    );
  }

  if (props.type === "password") {
    return (
      <CompassPasswordInput
        ref={ref as React.Ref<HTMLInputElement>}
        {...sharedCompassFieldProps}
        rootClassName={classNames(
          allowedClassesFor("TextInput", wrapperClassName ?? ""),
        )}
        {...omit(rest, ["type"])}
      />
    );
  }

  return (
    <CompassTextInput
      ref={ref as React.Ref<HTMLInputElement>}
      {...sharedCompassFieldProps}
      startContent={buildPrefixSlot(prefix, prefixWrapperClassName)}
      endContent={buildSuffixSlot(
        suffix,
        suffixWrapperClassName,
        suffixInnerClassName,
      )}
      rootClassName={classNames(
        allowedClassesFor("TextInput", wrapperClassName ?? ""),
      )}
      {...rest}
    />
  );
});

Input.displayName = "Input";
